/**
 * Holding endpoints: CRUD, manual conversion, cost-basis source and manual
 * lots, expired-option cleanup.
 */

import type { CreateHoldingDto, Holding, UpdateHoldingDto } from '@luminaria/shared';
import { apiClient } from './client';
import { API_ENDPOINTS } from './endpoints';

/** PUT /holdings/:id also accepts the stored price and value. */
export type UpdateHoldingInput = UpdateHoldingDto & { price?: number; value?: number };

export interface ManualLot {
  id: string;
  date: string;
  quantity: number;
  pricePerShare: number;
}

export interface CostBasisUpdateData {
  costBasis?: number;
  costBasisSource: 'imported' | 'calculated' | 'manual';
  costBasisMethod?: 'fifo' | 'average';
  costBasisReviewed?: boolean;
}

/** The holding row the cost-basis endpoints return. */
interface CostBasisHolding {
  id: string;
  symbol: string;
  name?: string;
  quantity: number;
  currentCostBasis: number;
  costBasisSource: string;
  costBasisReviewed: boolean;
  lastCostBasisUpdate?: string;
  manualLotsCount: number;
}

interface CleanupExpiredOptionsResponse {
  deletedCount: number;
  deletedHoldings: Array<{
    id: string;
    symbol: string;
    accountId: string;
  }>;
}

export const holdingsApi = {
  async listByAccount(accountId: string): Promise<Holding[]> {
    const { data } = await apiClient.get<{ holdings: Holding[] }>(API_ENDPOINTS.HOLDINGS.BY_ACCOUNT(accountId));
    return data.holdings;
  },

  async listByPortfolio(portfolioId: string): Promise<Holding[]> {
    const { data } = await apiClient.get<{ holdings: Holding[] }>(API_ENDPOINTS.HOLDINGS.BY_PORTFOLIO(portfolioId));
    return data.holdings;
  },

  async create(accountId: string, dto: CreateHoldingDto): Promise<Holding> {
    const { data } = await apiClient.post<{ holding: Holding }>(API_ENDPOINTS.HOLDINGS.BY_ACCOUNT(accountId), dto);
    return data.holding;
  },

  async update(id: string, dto: UpdateHoldingInput): Promise<Holding> {
    const { data } = await apiClient.put<{ holding: Holding }>(API_ENDPOINTS.HOLDINGS.GET(id), dto);
    return data.holding;
  },

  async remove(id: string): Promise<void> {
    await apiClient.delete(API_ENDPOINTS.HOLDINGS.GET(id));
  },

  /** Convert a stale synced holding (missing from the latest sync) to a manual one. */
  async convertToManual(id: string): Promise<Holding> {
    const { data } = await apiClient.post<{ holding: Holding }>(API_ENDPOINTS.HOLDINGS.CONVERT_TO_MANUAL(id));
    return data.holding;
  },

  async updateCostBasis(id: string, update: CostBasisUpdateData): Promise<CostBasisHolding> {
    const { data } = await apiClient.put<{ holding: CostBasisHolding }>(API_ENDPOINTS.HOLDINGS.COST_BASIS(id), update);
    return data.holding;
  },

  async addManualLot(id: string, lot: Omit<ManualLot, 'id'>): Promise<ManualLot> {
    const { data } = await apiClient.post<{ lot: ManualLot }>(API_ENDPOINTS.HOLDINGS.MANUAL_LOTS(id), lot);
    return data.lot;
  },

  async removeManualLot(id: string, lotId: string): Promise<void> {
    await apiClient.delete(API_ENDPOINTS.HOLDINGS.MANUAL_LOT(id, lotId));
  },

  async cleanupExpiredOptions(): Promise<CleanupExpiredOptionsResponse> {
    const { data } = await apiClient.delete<CleanupExpiredOptionsResponse>(API_ENDPOINTS.HOLDINGS.CLEANUP_EXPIRED_OPTIONS);
    return data;
  },
};
